import { Search, UserPlus, X } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { isClosed } from "@/lib/appointments";
import { useAddAttendee, useContacts, useRemoveAttendee } from "@/lib/queries";
import type { AppointmentDetail } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";

/**
 * A class session's contacts (ACL-293), in the appointment's drawer: who has a seat, out of how
 * many, with a way to take one out and to add another while seats are left. A closed session
 * (cancelled or rescheduled) only lists them.
 */
export function ClassAttendees({ appointment: a }: { appointment: AppointmentDetail }) {
  const toast = useToast();
  const add = useAddAttendee();
  const remove = useRemoveAttendee();
  const [adding, setAdding] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const q = useDebounced(query.trim());
  const contacts = useContacts({ q: adding && q.length >= 2 ? q : undefined });
  const seated = new Set(a.attendees.map((x) => x.id));
  const matches = q.length >= 2 ? (contacts.data?.items ?? []).filter((c) => !seated.has(c.id)).slice(0, 6) : [];
  const closed = isClosed(a.status);
  const full = a.seats != null && a.attendees.length >= a.seats;
  const fail = (title: string) => (e: unknown) => toast.error(title, e instanceof ApiError ? e.message : "Please try again.");

  return (
    <div className="space-y-2">
      <p className="text-xs text-text-muted">
        Contacts · {a.attendees.length}
        {a.seats != null && ` of ${a.seats} seats`}
      </p>
      {a.attendees.length === 0 ? (
        <p className="text-sm text-text-muted">Nobody has a seat yet.</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {a.attendees.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
              <span className="min-w-0 text-sm">
                <Link to={`/contacts/${c.id}`} className="block truncate font-medium text-accent-11 hover:underline">
                  {c.name ?? c.phone}
                </Link>
                <span className="block text-xs text-text-muted">{c.phone}</span>
              </span>
              {!closed && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove ${c.name ?? c.phone}`}
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(
                      { id: a.id, leadId: c.id },
                      {
                        onSuccess: () => toast.success("Removed from the class", c.name ?? c.phone),
                        onError: fail("Could not remove the contact"),
                      },
                    )
                  }
                >
                  <X />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!closed &&
        (full ? (
          <p className="text-xs text-text-muted">The session is full.</p>
        ) : adding ? (
          <div className="space-y-1.5">
            <Input
              autoFocus
              leading={<Search className="size-3.5" />}
              placeholder="Search by name, email, or phone"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {matches.length > 0 && (
              <ul className="divide-y divide-border rounded-md border border-border">
                {matches.map((m) => {
                  const name = [m.first_name, m.last_name].filter(Boolean).join(" ") || m.phone;
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        disabled={add.isPending}
                        className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-surface-hover"
                        onClick={() =>
                          add.mutate(
                            { id: a.id, leadId: m.id },
                            {
                              onSuccess: () => {
                                toast.success("Added to the class", name);
                                setQuery("");
                                setAdding(false);
                              },
                              onError: fail("Could not add the contact"),
                            },
                          )
                        }
                      >
                        <span className="truncate text-text">{name}</span>
                        <span className="shrink-0 text-xs text-text-muted">{m.phone}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {q.length >= 2 && !contacts.isFetching && matches.length === 0 && (
              <p className="text-xs text-text-muted">No contact matches.</p>
            )}
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
            <UserPlus />
            Add contact
          </Button>
        ))}
    </div>
  );
}
